/**
 * Cart edit line item dialog
 * ----------------------------------------------------------------------------
 * Custom element <cart-edit-modal> that lets the customer change the variant
 * of a cart line item from the cart page. Markup is rendered server-side by
 * `snippets/cart-edit-dialog.liquid`; this script only intercepts the form
 * submit, removes the current line via /cart/change.js (qty=0 on the line key)
 * and re-adds the chosen variant via /cart/add.js with the same quantity, then
 * reloads the page.
 *
 * Built from src/js/cart-edit-line-item-dialog.js into
 * src/assets/scripts-cart-edit-line-item-dialog.min.js and loaded site-wide
 * from src/snippets/scripts.liquid.
 */
(function () {
  'use strict';

  function defineCartEditModal() {
    var ModalCtor = window.customElements.get('x-modal');
    if (!ModalCtor || window.customElements.get('cart-edit-modal')) {
      return;
    }

    var CartEditModal = function () {
      return Reflect.construct(ModalCtor, [], CartEditModal);
    };
    Object.setPrototypeOf(CartEditModal.prototype, ModalCtor.prototype);
    Object.setPrototypeOf(CartEditModal, ModalCtor);

    Object.defineProperties(CartEditModal.prototype, {
      lineKey: {
        get: function () {
          return this.dataset.lineKey;
        },
      },
      currentQuantity: {
        get: function () {
          return parseInt(this.dataset.currentQuantity || '1', 10);
        },
      },
    });

    CartEditModal.prototype.connectedCallback = function () {
      ModalCtor.prototype.connectedCallback.call(this);
      var submitButton = this.querySelector('[data-cart-edit-submit]');
      if (submitButton && !submitButton.dataset.cartEditBound) {
        submitButton.dataset.cartEditBound = 'true';
        submitButton.addEventListener('click', this._onEditSubmit.bind(this));
      }
      if (!this.dataset.cartEditVariantBound) {
        this.dataset.cartEditVariantBound = 'true';
        this.addEventListener('change', this._onVariantChange.bind(this));
      }
    };

    CartEditModal.prototype._onVariantChange = function (event) {
      var radio = event.target;
      if (!radio || !radio.matches || !radio.matches('[data-cart-edit-variant-radio]')) {
        return;
      }
      var hidden = this.querySelector('[data-cart-edit-variant]');
      if (hidden) {
        hidden.value = radio.value;
      }
      var label = this.querySelector('[data-cart-edit-variant-label]');
      if (label) {
        label.textContent = radio.dataset.variantLabel || radio.value;
      }
    };

    CartEditModal.prototype._onEditSubmit = async function (event) {
      event.preventDefault();
      var submitButton = event.currentTarget;
      var variantField = this.querySelector('[data-cart-edit-variant]');
      if (!variantField || !variantField.value) {
        if (variantField && typeof variantField.reportValidity === 'function') {
          variantField.reportValidity();
        }
        return;
      }
      submitButton.setAttribute('aria-busy', 'true');
      submitButton.disabled = true;
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

      var newVariantId = variantField.value;
      var quantityField = this.querySelector('[data-cart-edit-quantity]');
      var quantity = quantityField ? parseInt(quantityField.value, 10) || this.currentQuantity : this.currentQuantity;
      var root = window.Shopify.routes.root;

      try {
        var changeResponse = await fetch(root + 'cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ id: this.lineKey, quantity: 0 }),
        });
        if (!changeResponse.ok) {
          var err1 = await changeResponse.json().catch(function () {
            return {};
          });
          throw new Error(err1.description || changeResponse.statusText);
        }
        var addResponse = await fetch(root + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ items: [{ id: newVariantId, quantity: quantity }] }),
        });
        if (!addResponse.ok) {
          var err2 = await addResponse.json().catch(function () {
            return {};
          });
          throw new Error(err2.description || addResponse.statusText);
        }
        window.location.reload();
      } catch (error) {
        console.error('[cart-edit-modal] failed to update line item', error);
        submitButton.removeAttribute('aria-busy');
        submitButton.disabled = false;
        document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
        this.dispatchEvent(new CustomEvent('cart:error', { bubbles: true, detail: { error: error.message } }));
      }
    };

    CartEditModal.prototype.createEnterAnimationControls = function () {
      return this.animate({ opacity: [0, 1] }, { duration: 200, easing: 'ease-out', fill: 'both' });
    };

    CartEditModal.prototype.createLeaveAnimationControls = function () {
      return this.animate({ opacity: [1, 0] }, { duration: 200, easing: 'ease-in', fill: 'both' });
    };

    window.customElements.define('cart-edit-modal', CartEditModal);
  }

  if (window.customElements && window.customElements.whenDefined) {
    window.customElements.whenDefined('x-modal').then(defineCartEditModal);
  }
})();
